import { useDispatch, useSelector } from 'react-redux'
import { DashboardSyncEvents, DashboardSyncSettings, IWidget, WidgetProps, WidgetSettings } from '../../widgetDashboard.interface'
import { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { selectChartConfig, selectChartTimeBucket, selectMultiStrikeChartConfigExpiry, selectTimeBuckets } from '../../../../selectors/chart'
import { getIsWidgetSlotActiveSelector } from '../../../../selectors/widgetDashboard'
import { deferAfterPaneSelectionPaint } from '../../deferPaneSideEffects'
import store from '../../../../store'
import useSetPrimarySecurity from '../../../../hooks/useSetPrimarySecurity'
import { deleteChartConfig, setActiveChart, updateChartConfig, updateChartDrawings, updateChartTimeBucket, updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { updateWidgetSettings, updateWidgetType } from '../../../../reducer/widgetDashboard.reducer'
import { AppContext } from '../../../../App'
import { CrossHairInfo, getDefaultChartConfig, getTimeIndex, ViewportCandleInfo } from '../../../../util/chart'
import { TimeDurations } from '../../../screener/StockScreener'
import { SuperChartConfiguration_TimeBucket } from '../../../../proto/generated/superchart-page'
import { TimeBucket_TimeSeries } from '../../../../proto/generated/common'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from '../../../../ErrorFallback'
import ChartWrapper from '../../../multiStikeChart/chartWrapper'
import { fetchOptionExpiriesV2 } from '../../../../util/options'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import { Box, Button, Stack, Typography } from '@mui/material'
import { WidgetTypes } from '../../widgetDashboard.enums'
import { selectActiveLayoutId } from '../../../../selectors/home'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { convertToUTC } from '../../../../util/dateTime'
import { resolveMultiStrikeTimeBucket } from '../../../../util/timeBuckets'
import MultiStrikeChartSkeletonOverlay from '../../../common/MultiStrikeChartSkeletonOverlay'
import { Segment } from '../../../../proto/generated/security'
import { isExpressionMode } from '../../../../util/expression'
import MultiStrikeSettingsDialog from '../../../widgetDashboard/widgets/multiStrikeWidget/multiStrikeSettingsDialog'

export type ChartWidgetSettings = WidgetSettings<{
    chartId: string
    timeBucket?: SuperChartConfiguration_TimeBucket
}>

const MultiStrikeChartWidget = forwardRef<any, WidgetProps<ChartWidgetSettings>>(({ dashboardId, slotId, settings, hoveredSlotId, allowSplit }, ref) => {
    const dispatch = useDispatch()
    const chartId = settings.chartId ?? `${dashboardId}-${slotId}`
    const appContext = useContext(AppContext)
    const setPrimarySecurity = useSetPrimarySecurity()
    const mounted = useRef(false)
    const removed = useRef(false)
    const [loading, setLoading] = useState(false)
    const [securityLoading, setSecurityLoading] = useState(true)
    const [openSettings, setOpenSettings] = useState(false)
    const { key, expressionMode, securities, timeBucket } = settings
    const isActiveSlot = useSelector(getIsWidgetSlotActiveSelector(dashboardId, slotId))
    const chartBucket = useSelector(selectChartTimeBucket(chartId))
    const timeBuckets = useSelector(selectTimeBuckets)
    const previousSecurityInfo = useRef({ key, expressionMode, securities })
    const previousChartBucket = useRef(chartBucket)
    const previousCandleInfo = useRef<{ startTime: number; endTime: number }>(null)
    const previousKey = useRef('')
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => {
        if (!key) return
        let cancelled = false
        setSecurityLoading(true)
        if (isExpressionMode(key)) {
            setLoading(false)
            setSecurityLoading(false)
            return
        }
        getSecurityAsync(key).then(async (newSecurity) => {
            if (cancelled) return
            if (!newSecurity) {
                setSecurity(undefined)
                setSecurityLoading(false)
                setLoading(false)
                return
            }
            if ([Segment.FO, Segment.CD].includes(newSecurity.segment)) {
                const underlying = await getSecurityAsync(getSecurityKey(newSecurity.exchange, newSecurity.underlyingSecurityCode ?? ''))
                if (!cancelled) setSecurity(underlying ?? newSecurity)
            } else {
                if (!cancelled) setSecurity(newSecurity)
            }
            if (!cancelled) setSecurityLoading(false)
        })
        return () => { cancelled = true }
    }, [key])

    const securityKey = useMemo(() => security ? getSecurityKey(security.exchange, security.code) : '', [security]);
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(securityKey))
    const expiry = useSelector(selectMultiStrikeChartConfigExpiry(chartId))
    const activeLayoutId = useSelector(selectActiveLayoutId)

    const chartConfig = useSelector(selectChartConfig(chartId));

    useEffect(() => {
        if (!chartConfig) {
            dispatch(
                updateChartConfig({
                    viewKey: chartId,
                    config: getDefaultChartConfig()
                })
            )
        }
    }, [chartConfig])

    useEffect(() => {
        const mount = !!security;

        if (isActiveSlot && mount && !mounted.current && chartBucket) {
            mounted.current = true
            previousChartBucket.current = chartBucket
            deferAfterPaneSelectionPaint(() => {
                dispatch(
                    setActiveChart({
                        chartId,
                        chartFocused: true
                    })
                )
                const { key: currentKey, expressionMode: currentExpressionMode } =
                    store.getState().homeStore.primarySecurity
                if (currentKey !== securityKey || currentExpressionMode !== expressionMode) {
                    setPrimarySecurity(
                        securityKey,
                        [security],
                        expressionMode,
                        security?.displayName ?? security?.symbol
                    )
                }
            })
        }
    }, [securityKey, security, isActiveSlot, slotId, expressionMode, chartId, chartBucket])

    useEffect(() => {
        if (!loading) return
        if (optionsExpiries?.length) {
            setLoading(false)
        }
    }, [loading, optionsExpiries])

    useEffect(() => {
        if (!expiry && optionsExpiries && optionsExpiries?.length > 0) {
            dispatch(
                updateMultiStrikeChartConfig({
                    viewKey: chartId,
                    config: { expiry: optionsExpiries.filter((o) => !o.derived)[0]?.expiry?.name }
                })
            )
        }
    }, [optionsExpiries, expiry, chartId, dispatch])

    useEffect(() => {
        if (!securityKey || !security) return

        if (securityKey !== previousKey.current && !optionsExpiries?.length) {
            setLoading(true)
            let sec = security;
            if (sec?.underlyingSecurityCode) {
                getSecurityAsync(getSecurityKey(sec.exchange, sec.underlyingSecurityCode)).then((underlying) => {
                    const secToUse = underlying ?? sec
                    fetchOptionExpiriesV2(secToUse, dispatch).then(() => {
                        previousKey.current = securityKey
                    })
                })
                return
            }
            fetchOptionExpiriesV2(sec, dispatch).then(() => {
                previousKey.current = securityKey
            })
        }
    }, [securityKey, security, optionsExpiries?.length])

    const onMessageReceived = useCallback(
        (event: MessageEvent<any>) => {
            if (!!sessionStorage.getItem('messageProcessed')) return
            if (event.origin !== window.location.origin) {
                return
            }
            if (event.data?.source === 'parentWindow') {
                const receivedDuration = event?.data?.payload?.timeBucket as TimeDurations
                sessionStorage.setItem('messageProcessed', 'true')
                if (receivedDuration) {
                    let newTimeBucket: SuperChartConfiguration_TimeBucket | undefined
                    if (receivedDuration === '5m') {
                        newTimeBucket = timeBuckets.find((t) => t.timeSeries === TimeBucket_TimeSeries.MINUTE && t.timeValue === 5)
                    } else if (receivedDuration === '30m') {
                        newTimeBucket = timeBuckets.find((t) => t.timeSeries === TimeBucket_TimeSeries.MINUTE && t.timeValue === 30)
                    } else if (receivedDuration === '1h') {
                        newTimeBucket = timeBuckets.find((t) => t.timeSeries === TimeBucket_TimeSeries.HOUR && t.timeValue === 1)
                    } else if (receivedDuration === '1D') {
                        newTimeBucket = timeBuckets.find((t) => t.timeSeries === TimeBucket_TimeSeries.DAY && t.timeValue === 1)
                    }
                    if (newTimeBucket) {
                        const newBucket = {
                            viewKey: chartId,
                            timeBucket: newTimeBucket
                        }
                        dispatch(updateChartTimeBucket(newBucket))
                    }
                }
            }
        },
        [chartId, timeBuckets]
    )

    useEffect(() => {
        if (window.opener) {
            window.opener.postMessage({ ready: true, component: 'ChildComponent' }, window.location.origin)
            window.addEventListener('message', onMessageReceived)
        }
        return () => {
            if (window.opener) {
                window.removeEventListener('message', onMessageReceived)
            }
        }
    }, [onMessageReceived])

    useImperativeHandle<any, IWidget>(
        ref,
        () => {
            return {
                onActive() {
                    deferAfterPaneSelectionPaint(() => {
                        dispatch(
                            setActiveChart({
                                chartId,
                                chartFocused: true
                            })
                        )
                        const { key: currentKey, expressionMode: currentExpressionMode } =
                            store.getState().homeStore.primarySecurity
                        if (currentKey !== securityKey || currentExpressionMode !== expressionMode) {
                            setPrimarySecurity(
                                securityKey,
                                security ? [security] : [],
                                expressionMode,
                                security?.displayName ?? security?.symbol
                            )
                        }
                    })
                },
                onBlur() {
                    const chart = appContext.getMultiStrikeChart(chartId)
                    chart && chart.onBlur()
                },
                openSettings() {
                    setOpenSettings(true)
                },
                updateSettings(settings: WidgetSettings<any>) {
                    dispatch(
                        updateWidgetSettings({
                            id: dashboardId,
                            slotId,
                            settings
                        })
                    )
                },
                onSyncSettingsChanged(settingKey: keyof DashboardSyncSettings, value: boolean) {
                    switch (settingKey) {
                        case 'security':
                            value &&
                                appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                                    key: securityKey,
                                    expressionMode,
                                    securities: [securityKey]
                                })
                            break
                        case 'timeBucket':
                            value && appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.TIME_BUCKET, previousChartBucket.current)
                    }
                },
                onRemove() {
                    removed.current = true
                },
                onSecurityUpdate: (data) => {
                    dispatch(
                        updateWidgetSettings({
                            id: dashboardId,
                            slotId,
                            settings: {
                                ...data
                            }
                        })
                    )
                },
                onTimeBucketUpdate: (timeBucket) => {
                    if (!timeBucket) return
                    const resolved = resolveMultiStrikeTimeBucket(timeBucket, timeBuckets) ?? timeBucket
                    dispatch(
                        updateChartTimeBucket({
                            viewKey: chartId,
                            timeBucket: resolved
                        })
                    )
                },
                onCrosshairUpdate: ([price, time]) => {
                    const chart = appContext.getMultiStrikeChart(chartId)
                    if (!chart) return
                    if (!time && !price) {
                        chart.clearSyncedCrosshair()
                        return
                    }
                    let crossHairTime = time
                    if (!time) {
                        const data = chart.getRenderedPrimaryData()
                        if (data?.length) {
                            crossHairTime = data[data.length - 1].time as number
                        }
                    }
                    chart.setCrossHairPosition(price, crossHairTime)
                },
                onTimeUpdate: ({ startTime, endTime, timeBucket, rangeStart, rangeEnd, barsBefore, barsAfter, gotoDate, middlePointTime }) => {
                    const chart = appContext.getMultiStrikeChart(chartId)
                    if (timeBucket.code !== chart.timeBucket.code) {
                        Number.isFinite(middlePointTime) && chart.goToDate(convertToUTC(middlePointTime!!, chart.timezone))
                    } else {
                        chart.focusTimeRange(
                            {
                                startTime,
                                endTime,
                                rangeStart,
                                rangeEnd,
                                middlePointTime,
                                barsBefore,
                                barsAfter,
                                viewportCandles: rangeEnd - rangeStart
                            },
                            timeBucket,
                            gotoDate
                        )
                    }
                },
                onDateUpdate: (date) => {
                    const chart = appContext.getMultiStrikeChart(chartId)
                    chart?.goToDate(convertToUTC(date, chart?.timezone), true)
                }
            }
        },
        [chartId, setPrimarySecurity, securityKey, security, expressionMode, appContext, dashboardId, slotId, timeBuckets, dispatch]
    )

    const getSyncCrosshairPayload = useCallback(
        (data: CrossHairInfo): [number, number] => {
            if (!data?.time) {
                return [0, 0]
            }
            const chart = appContext.getMultiStrikeChart(chartId)
            const primaryData = chart?.getRenderedPrimaryData()
            if (primaryData?.length) {
                const index = getTimeIndex(primaryData, data.time)
                if (Number.isFinite(index) && primaryData[index]) {
                    const value = (primaryData[index] as { value?: number }).value
                    if (Number.isFinite(value)) {
                        return [value as number, data.time]
                    }
                }
            }
            return [data.price || 0, data.time]
        },
        [appContext, chartId]
    )

    const onCrosshairMove = useCallback(
        (data?: CrossHairInfo) => {
            if (hoveredSlotId !== slotId) return
            if (!data?.point) {
                appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.CROSSHAIR, [0, 0])
                return
            }
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.CROSSHAIR, getSyncCrosshairPayload(data))
        },
        [slotId, hoveredSlotId, appContext.dashboard, getSyncCrosshairPayload]
    )

    const onTimeRangeChange = ({ startTime, endTime, rangeStart, rangeEnd, barsBefore, barsAfter, middlePointTime }: ViewportCandleInfo, gotoDate: number | undefined, initial = false) => {
        if (gotoDate) {
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.DATE, gotoDate)
        } else if (Number.isFinite(hoveredSlotId) && hoveredSlotId === slotId) {
            const chart = appContext.getMultiStrikeChart(chartId)
            if (chart && (!previousCandleInfo.current || previousCandleInfo.current.startTime !== startTime || previousCandleInfo.current.endTime !== endTime)) {
                appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.TIME, {
                    startTime,
                    endTime,
                    rangeStart,
                    rangeEnd,
                    barsBefore,
                    barsAfter,
                    middlePointTime,
                    timeBucket: chart.timeBucket,
                    gotoDate: gotoDate,
                    viewportCandles: rangeEnd - rangeStart
                })
                previousCandleInfo.current = { startTime, endTime }
            }
        }
    }

    useEffect(() => {
        if (!isActiveSlot) return
        if (securityKey && securityKey !== previousSecurityInfo.current.key) {
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                key: securityKey,
                expressionMode,
                securities: [securityKey]
            })
            previousSecurityInfo.current = {
                key: securityKey,
                expressionMode,
                securities: [securityKey]
            }
        }
    }, [slotId, securityKey, expressionMode, isActiveSlot])

    useEffect(() => {
        if (!previousChartBucket.current || !chartBucket) return

        if (previousChartBucket.current.code !== chartBucket.code) {
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.TIME_BUCKET, chartBucket)
            previousChartBucket.current = { ...chartBucket }
        }
    }, [chartBucket])

    useEffect(() => {
        return () => {
            if (removed.current) {
                dispatch(
                    updateChartDrawings({
                        viewKey: chartId,
                        drawings: []
                    })
                )
                dispatch(deleteChartConfig(chartId))
            }
        }
    }, [])

    if (!chartId) return null

    const handleSecuritySelected = useCallback(
        (key: string, expressionMode: boolean, securities: string[]) => {
            dispatch(
                updateWidgetSettings({
                    id: dashboardId,
                    slotId,
                    settings: {
                        key,
                        expressionMode,
                        securities,
                    },
                })
            )
        },
        [dashboardId, dispatch, slotId]
    )

    return loading || securityLoading ? (
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    bgcolor: 'background.paper',
                }}
            >
                <MultiStrikeChartSkeletonOverlay />
            </Box>
        ) : (
        <>
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onReset={(reset) => {
                //@ts-ignore
                if (reset?.args?.[0]) {
                    dispatch(
                        updateChartConfig({
                            viewKey: chartId,
                            config: getDefaultChartConfig()
                        })
                    )
                } else {
                    setTimeout(() => {
                        window.location.reload()
                    }, 10)
                }
            }}
        >
            {security?.hasFo && chartConfig ? (
                <ChartWrapper
                    key={chartId}
                    allowSplit={allowSplit}
                    viewKey={chartId}
                    isWidget
                    securityList={securityKey ? [securityKey] : []}
                    onCrosshairMove={onCrosshairMove}
                    onTimeRangeChange={onTimeRangeChange}
                    onSecuritySelected={handleSecuritySelected}
                />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        background: (theme) => theme.palette.background.paper
                    }}
                >
                    <Stack gap={2}>
                        <Typography>F&O not present for this security</Typography>
                        <Button
                            variant={'outlined'}
                            onClick={() => {
                                dispatch(
                                    updateWidgetType({
                                        id: activeLayoutId,
                                        slotId: Number(slotId),
                                        type: WidgetTypes.CHART
                                    })
                                )
                            }}
                        >
                            Go to candle chart
                        </Button>
                    </Stack>
                </Box>
            )}
        </ErrorBoundary>
        <MultiStrikeSettingsDialog
            open={openSettings}
            onClose={() => setOpenSettings(false)}
            viewKey={chartId}
        />
        </>
    )
})

MultiStrikeChartWidget.displayName = 'ChartWidget'

export default memo(MultiStrikeChartWidget)
